import { useAtomValue, useSetAtom } from "jotai";
import { roomAtom } from "../../core/atoms";
import { useCallback, useEffect } from "react";
import { RemoteDataComponentProps } from "../../core/types";
import { useMessageEventHandlerCallback as useBaseMessageEventHandlerCallback } from "../../core/message/useMessageEventHandlerCallback";
import { ZoomRemoteDataDelegator as BaseZoomRemoteDataDelegator } from "../room/participant/media/RemoteDataDelegator";
import { useAtomCallback } from "jotai/utils";
import { ZoomRemoteDataDelegatorAtom } from "./state";

export interface UseZoomCommandChannelMessageListenerOptions
  extends Pick<RemoteDataComponentProps, "onMessageReceived"> {
  /**
   * Dependecy injection useful for testing.
   */
  useMessageEventHandlerCallback?: typeof useBaseMessageEventHandlerCallback;
  /**
   * Dependecy injection useful for testing.
   */
  ZoomRemoteDataDelegator?: typeof BaseZoomRemoteDataDelegator;
}

/**
 * Registers one command channel message listener for the Zoom room.
 */
export function useZoomCommandChannelMessageListener({
  onMessageReceived,
  useMessageEventHandlerCallback = useBaseMessageEventHandlerCallback,
  ZoomRemoteDataDelegator = BaseZoomRemoteDataDelegator,
}: UseZoomCommandChannelMessageListenerOptions) {
  const room = useAtomValue(roomAtom);
  const handleReceivedMessage = useMessageEventHandlerCallback({
    onMessageReceived,
  });
  const getZoomRemoteDataDelegator = useAtomCallback(
    useCallback((get) => get(ZoomRemoteDataDelegatorAtom), []),
  );
  const setZoomRemoteDataDelegator = useSetAtom(ZoomRemoteDataDelegatorAtom);

  useEffect(
    function handleCommandChannelMessageListener() {
      const existingZoomRemoteDataDelegator = getZoomRemoteDataDelegator();

      // It's not mutex air-tight but should be good enough
      if (!room || existingZoomRemoteDataDelegator) return;

      const dataDelegator = new ZoomRemoteDataDelegator();
      setZoomRemoteDataDelegator(dataDelegator);

      dataDelegator.on("message", handleReceivedMessage);

      return function cleanup() {
        dataDelegator.off();
        setZoomRemoteDataDelegator(null);
      };
    },
    [
      ZoomRemoteDataDelegator,
      getZoomRemoteDataDelegator,
      handleReceivedMessage,
      room,
      setZoomRemoteDataDelegator,
    ],
  );
}
